'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import GlobalLoader from '../GlobalLoader';
import Input from '../Input';
import Button from '../Button';
import useUserContext from '@/hooks/useUserContext';
import { login } from '@/actions/auth';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { DEV_URL } from '@/utils/url';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/useLocale';

const schema = yup.object().shape({
  username: yup.string().required(),
  password: yup.string().required(),
  remember_me: yup.boolean().oneOf([true, false], ''),
});

type Props = {
  isHeader?: boolean;
  dict: IModalsDictionary;
  atLoginPage?: boolean;
};

export default function LoginForm({
  isHeader = false,
  dict,
  atLoginPage = false,
}: Props) {
  const [error, setError] = useState<string>();
  const { setUser, setIsLogged, setUserGrant } = useUserContext();
  const lang = useLocale();
  const { push } = useRouter();
  const searchParams = useSearchParams();

  const redirectParam = searchParams.get('r');

  const {
    register,
    formState: { isSubmitting },
    setValue,
    handleSubmit,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async ({ username, password, remember_me }) => {
    try {
      const response = await login({ username, password, lang });
      const user = response.user;

      if (user) {
        const userGrant = user.user_grant;

        setUser(user);
        setIsLogged(true);
        setUserGrant(userGrant);

        if (userGrant == 'owner') {
          return push(
            `${DEV_URL}/?token=${response.session_id}&url=${encodeURIComponent(
              redirectParam ?? '/owner/dashboard',
            )}`,
          );
        }
        if (userGrant == 'admin') {
          return push(
            `${DEV_URL}/?token=${response.session_id}&url=${encodeURIComponent(
              redirectParam ?? '/admin/dashboard',
            )}`,
          );
        }

        const langPart =
          user.languageCode === 'en' ? '' : `/${user.languageCode}`;

        return push(
          `${DEV_URL}${lang == 'en' ? '' : `/${lang}`}/?token=${
            response.session_id
          }&url=${encodeURIComponent(redirectParam ?? `${langPart}/customer/dashboard`)}`,
        );
      }

      if (response.message) {
        setError(response.message);
      }
    } catch (error) {
      // Login now depends on next-admin being reachable. Without this the whole
      // failure is a console line and the button just does nothing.
      console.log(error);
      setError(dict.loginModal.genericError);
    }
  });

  return (
    <div
      className={cn('flex flex-col px-[60px] py-[20px] relative', {
        'py-0 px-0 tablet:py-[20px] tablet:px-[60px]': isHeader,
        'py-0 px-0': atLoginPage,
      })}>
      {!atLoginPage && <GlobalLoader isLoading={isSubmitting} />}
      <h2 className="mb-8 text-4xl leading-[1.25] font-semibold">
        {dict.loginModal.title}
      </h2>
      <form onSubmit={onSubmit} className="flex flex-col gap-8">
        <div className="flex flex-col gap-[1rem]">
          <Input.Container>
            <Input.Label htmlFor="username">
              {dict.loginModal.username}
            </Input.Label>
            <Input.Root register={register} name="username" />
          </Input.Container>
          <Input.Container>
            <Input.Label htmlFor="password">
              {dict.loginModal.password}
            </Input.Label>
            <Input.Root register={register} name="password" type="password" />
            {error && (
              <label className="text-error-primary font-normal text-[.8125rem] italic leading-[1.8] mt-2">
                {error}
              </label>
            )}
          </Input.Container>
          <div className="flex items-start gap-2 md:w-[20rem] xl:w-[30rem] desktop:w-full">
            <input
              id="remember_me"
              {...register('remember_me')}
              className={cn('checkbox-input w-6 h-6', {
                'border border-underline': watch('remember_me') == true,
              })}
              onChange={(e) => setValue('remember_me', e.target.checked)}
              type="checkbox"
              name="checkbox-input"
            />
            <label
              className="text-[.875rem] leading-[1.8] text-gray-700 cursor-pointer"
              htmlFor="remember_me">
              {dict.loginModal.remember}
            </label>
          </div>
        </div>
        <Button>{dict.loginModal.button}</Button>
      </form>
      <a
        href={
          lang == 'en' ? '/resetting/request' : `/${lang}/resetting/request`
        }
        className="mt-2 text-center text-primary ">
        {dict.loginModal.forgotPassword}
      </a>
    </div>
  );
}
